import { getStoredToken } from './auth'
import { requestVoid } from './http'

export const personnelScopes = ['broker', 'brokerPersonnelManager', 'brokerPricingAdministrator'] as const

export type PersonnelScope = (typeof personnelScopes)[number]

export interface PersonnelListItem {
  id: string
  scope: PersonnelScope[]
  user?: {
    id?: string
    givenName?: string
    surname?: string
    personalNumber?: string | number
  }
}

const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

type RequestOptions = {
  method?: 'GET' | 'POST'
  body?: Record<string, unknown>
}

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  if (!API_BASE_URL) {
    throw new Error('Missing API base URL (VITE_DAPI_URL).')
  }

  const token = getStoredToken()

  if (!token?.jwt) {
    throw new Error('Missing authentication token.')
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      authorization: `bearer ${token.jwt}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  if (!response.ok) {
    let message = 'Request failed'

    try {
      const data = (await response.json()) as { message?: string; error?: { message?: string } }
      if (data?.message) {
        message = data.message
      }
      if (data?.error?.message) {
        message = `${message}: ${data.error.message}`
      }
    } catch {
      // Keep fallback message.
    }

    throw new Error(message)
  }

  return (await response.json()) as T
}

export const fetchPersonnel = async (): Promise<PersonnelListItem[]> => {
  return await request<PersonnelListItem[]>('/borgo/distributor/personnel')
}

export const createPersonnel = async (payload: {
  personalNumber: string
  scope: PersonnelScope[]
}): Promise<PersonnelListItem> => {
  return await request<PersonnelListItem>('/borgo/distributor/personnel', {
    method: 'POST',
    body: payload,
  })
}

export const updatePersonnel = async (
  id: string,
  payload: {
    scope: PersonnelScope[]
  },
): Promise<PersonnelListItem> => {
  return await request<PersonnelListItem>(`/borgo/distributor/personnel/${id}`, {
    method: 'POST',
    body: payload,
  })
}

export const deletePersonnel = async (id: string): Promise<void> => {
  await requestVoid(`/borgo/distributor/personnel/${id}`, 'DELETE')
}
